import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconBuildingStore, IconPencil, IconSpeakerphone } from '@tabler/icons-react';
import PosterEditor from '@/components/common/PosterEditor';
import SelectField from '@/components/common/SelectField';
import DatePickerField from '@/components/common/DatePickerField';
import { Field, GroupTitle, LogoField, TextField, inputCls } from '@/components/common/EditorFields';
import { fileStem } from '@/utils/posterExport';
import { imageUrlToDataUrl } from '@/utils/downloadPoster';
import { toBnDigits } from '@/utils/bnFormat';
import questionPaperService from '@/services/questionPaperService';
import { useBiggaponStyleStore } from '@/store/biggaponStyleStore';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import BiggaponTemplate from './templates';
import { getTheme } from './templates/themes';
import BiggaponStyleGrid from './BiggaponStyleGrid';

// A4 at 96 dpi-ish — the poster is laid out at this width (its type scales with the width, see PosterBase) and only ever scaled from outside.
const PORTRAIT = { w: 595, h: 842 };
const LANDSCAPE = { w: 842, h: 595 };
const NO_EDITS = {};

// A notice's dates are ISO strings; the picked day goes back as one (local midnight), which the poster formats in local time —
// the same way it formats the notice's own dates, so what the picker shows is what the poster prints.
const toDate = (v) => {
    if (!v) return null;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
};

// Remote images (files.sohozkaj.com) can't be read by the export capture — they are swapped for data URLs up front.
// A fresh CORS fetch first (that is how the old per-card download did it), the backend image proxy if the browser refuses.
async function toDataUrl(url) {
    try {
        return await imageUrlToDataUrl(url);
    } catch {
        return questionPaperService.proxyImageDataUrl(url);
    }
}

function useDataUrls() {
    const cache = useRef(new Map());   // url → data URL
    const pending = useRef(new Map()); // url → Promise
    const [version, setVersion] = useState(0);
    const ensure = useCallback((url) => {
        if (!url || url.startsWith('data:') || (url.startsWith('/') && !url.startsWith('//'))) return Promise.resolve(url);
        if (cache.current.has(url)) return Promise.resolve(cache.current.get(url));
        if (!pending.current.has(url)) {
            pending.current.set(url, toDataUrl(url)
                .then((data) => { cache.current.set(url, data); setVersion((n) => n + 1); return data; })
                .catch(() => { pending.current.delete(url); return url; })); // not remembered as done: the next ensure() tries again
        }
        return pending.current.get(url);
    }, []);
    const resolve = useCallback((url) => (url && cache.current.get(url)) || url, []);
    return { ensure, resolve, version };
}

/**
 * The biggapon poster editor: pick a notice from the list, pick one of the existing poster themes, change the wording and the
 * shop footer, then download (JPG / PDF) or print. The editor window itself is the shared <PosterEditor>.
 *
 * Edits are for the poster only — the job notice and the shop profile are never touched, and the edits are not kept once the
 * window closes. The chosen THEME is the viewer's usual saved one (biggapon-style store), as it always was.
 *   jobs      full job notices (they carry the poster fields)      business  { shopName, businessAddress, phone, shopLogo }
 *   onClose   the red close button of the window — for the ready-made window with the notices loaded see <BiggaponModal>
 */
export default function BiggaponEditor({ jobs, business, onClose }) {
    const { t, i18n } = useTranslation();
    const digits = (n) => (i18n.language?.startsWith('en') ? String(n) : toBnDigits(n));

    const styleId = useBiggaponStyleStore((s) => s.styleId);
    const setStyleId = useBiggaponStyleStore((s) => s.setStyleId);
    const [jobId, setJobId] = useState(() => String(jobs[0]?.id));
    const [overrides, setOverrides] = useState({});   // jobId → the fields the user changed on that poster
    const [shopEdits, setShopEdits] = useState({});   // shopName / businessAddress / phone / shopLogo the user changed (all posters)
    const { ensure, resolve, version } = useDataUrls();

    const baseJob = useMemo(() => jobs.find((j) => String(j.id) === jobId) || jobs[0], [jobs, jobId]);
    const edits = overrides[baseJob.id] || NO_EDITS;
    const setEdit = (patch) => setOverrides((prev) => ({ ...prev, [baseJob.id]: { ...(prev[baseJob.id] || NO_EDITS), ...patch } }));
    const setShop = (patch) => setShopEdits((prev) => ({ ...prev, ...patch }));

    // what the poster draws: the notice + its edits, and the shop footer + its edits (logos as data URLs once they are ready)
    const job = useMemo(() => ({ ...baseJob, ...edits, logoUrl: resolve(baseJob.logoUrl) }), [baseJob, edits, resolve, version]); // eslint-disable-line react-hooks/exhaustive-deps
    const shopLogoUrl = shopEdits.shopLogo ?? business?.shopLogo ?? '';
    const poster = useMemo(() => ({ ...business, ...shopEdits, shopLogo: resolve(shopLogoUrl) }), [business, shopEdits, shopLogoUrl, resolve, version]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { ensure(baseJob.logoUrl); ensure(shopLogoUrl); }, [baseJob.logoUrl, shopLogoUrl, ensure]);

    const theme = getTheme(styleId);
    const landscape = theme.orientation === 'landscape';
    const pageSize = landscape ? LANDSCAPE : PORTRAIT;
    const index = jobs.indexOf(baseJob);
    const edited = Object.keys(edits).length > 0;

    const value = (key) => edits[key] ?? baseJob[key] ?? '';

    const year = new Date().getFullYear();
    const dateField = (key, label) => {
        const date = toDate(value(key));
        const shown = date?.getFullYear() ?? year;
        return (
            <Field label={label}>
                <DatePickerField
                    value={date}
                    onChange={(d) => { if (d) setEdit({ [key]: d.toISOString() }); }}
                    triggerClassName={inputCls}
                    placeholder={t('placeholders.selectDate')}
                    fromYear={Math.min(year - 1, shown)}
                    toYear={Math.max(year + 2, shown)}
                />
            </Field>
        );
    };

    // ── panels ──
    const posterPanel = (
        <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
                <GroupTitle>{t('biggapon.editor.pickPoster')}</GroupTitle>
                <SelectField
                    value={jobId}
                    onChange={setJobId}
                    triggerClassName={inputCls}
                    options={jobs.map((j) => ({ value: String(j.id), label: [j.organization, j.position].filter(Boolean).join(' — ') || String(j.id) }))}
                />
            </div>
            <div className="flex flex-col gap-2">
                <GroupTitle>{t('biggapon.stylePicker.label')}</GroupTitle>
                <BiggaponStyleGrid value={theme.id} onChange={setStyleId} sampleJob={baseJob} business={business} />
            </div>
        </div>
    );

    const textPanel = (
        <div className="flex flex-col gap-3">
            {/* the caps are the notice's own column sizes, so a notice's existing text is never cut by the first keystroke */}
            <Field label={t('biggapon.editor.fields.organization')}>
                <TextField value={value('organization')} onChange={(v) => setEdit({ organization: v })} maxLen={255} />
            </Field>
            <Field label={t('biggapon.editor.fields.position')}>
                <TextField value={value('position')} onChange={(v) => setEdit({ position: v })} maxLen={500} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
                {dateField('publishedDate', t('biggapon.editor.fields.startDate'))}
                {dateField('deadlineDate', t('biggapon.editor.fields.endDate'))}
            </div>
            {/* free text on the notice ("৫০+", "প্রায় ১০০") — the poster writes it as given */}
            <Field label={t('biggapon.editor.fields.vacancies')}>
                <TextField value={String(value('totalVacancies'))} onChange={(v) => setEdit({ totalVacancies: v })} maxLen={50} />
            </Field>
            <Field label={t('biggapon.editor.fields.education')}>
                <TextField multiline value={value('posterQualification')} onChange={(v) => setEdit({ posterQualification: v })} maxLen={500} />
            </Field>
            <Field label={t('biggapon.editor.fields.age')}>
                <TextField value={value('posterBosoyShima')} onChange={(v) => setEdit({ posterBosoyShima: v })} maxLen={500} />
            </Field>
            {edited && (
                <button
                    type="button"
                    onClick={() => setOverrides((prev) => { const next = { ...prev }; delete next[baseJob.id]; return next; })}
                    className="self-start text-[13px] font-semibold text-primary hover:underline"
                >
                    {t('biggapon.editor.resetEdits')}
                </button>
            )}
        </div>
    );

    const shopPanel = (
        <div className="flex flex-col gap-3">
            <Field label={t('biggapon.editor.fields.shopName')}>
                <TextField value={poster.shopName || ''} onChange={(v) => setShop({ shopName: v })} maxLen={255} />
            </Field>
            <Field label={t('biggapon.editor.fields.address')}>
                <TextField value={poster.businessAddress || ''} onChange={(v) => setShop({ businessAddress: v })} maxLen={500} />
            </Field>
            <Field label={t('biggapon.editor.fields.phone')}>
                <TextField value={poster.phone || ''} onChange={(v) => setShop({ phone: v })} maxLen={30} inputMode="tel" />
            </Field>
            <LogoField value={shopLogoUrl} onChange={(v) => setShop({ shopLogo: v })} />
        </div>
    );

    const tabs = ['poster', 'text', 'shop'].map((id) => ({
        id,
        label: t(`biggapon.editor.tabs.${id}.label`),
        title: t(`biggapon.editor.tabs.${id}.title`),
        desc: t(`biggapon.editor.tabs.${id}.desc`),
        Icon: { poster: IconSpeakerphone, text: IconPencil, shop: IconBuildingStore }[id],
        content: { poster: posterPanel, text: textPanel, shop: shopPanel }[id],
    }));

    return (
        <PosterEditor
            icon={<IconSpeakerphone size={20} />}
            title={t('biggapon.pageTitle')}
            onClose={onClose}
            metaLine={t('biggapon.editor.metaLine', { n: digits(index + 1), total: digits(jobs.length) })}
            tabs={tabs}
            pageSize={pageSize}
            pageCount={1}
            fitKey={styleId}
            renderPages={({ preview }) => [
                <div key="poster" data-poster-page style={{ width: pageSize.w, boxShadow: preview ? '0 10px 40px rgba(0,0,0,.4)' : 'none' }}>
                    <BiggaponTemplate templateId={styleId} job={job} business={poster} />
                </div>,
            ]}
            exportConfig={{
                stem: fileStem(baseJob.slug || baseJob.id, 'biggapon'),
                sheetMm: landscape ? [297, 210] : [210, 297],
                imageFormat: 'jpeg',
                imagePixelRatio: Math.ceil((landscape ? 3508 : 2480) / pageSize.w), // ≥ A4 at 300 dpi (the sheet's short side is 2480 px, its long side 3508), like the old per-card download
                settleMs: 450,                                 // the poster fits its type to the sheet once fonts are in
                prepare: async () => {
                    await Promise.all([ensure(baseJob.logoUrl), ensure(shopLogoUrl)]);
                    await document.fonts?.ready;
                },
                meta: `A4 · ${t(landscape ? 'biggapon.editor.landscape' : 'biggapon.editor.portrait')}`,
            }}
        />
    );
}
